import React, { useState, useEffect, useCallback } from 'react';
import api from '../lib/api';
import { Plus, Trash2, Edit3, X, Users, Search, Clock, DollarSign, CalendarX, ShieldCheck, Heart, FileText, AlertTriangle, CheckCircle, Receipt } from 'lucide-react';

function isExpired(d) { return d && new Date(d) < new Date(); }
function isExpiringSoon(d) { if (!d) return false; const diff = (new Date(d) - new Date()) / 86400000; return diff > 0 && diff <= 30; }

function SalaryForm({ employeeId, onDone }) {
  const [form, setForm] = useState({ month: '', year: new Date().getFullYear(), base_salary: 0, extras: 0, deductions: 0, net_salary: 0, receipt_base64: '' });
  const set = (k, v) => setForm(p => {
    const n = { ...p, [k]: v };
    if (['base_salary','extras','deductions'].includes(k)) n.net_salary = (parseFloat(n.base_salary) || 0) + (parseFloat(n.extras) || 0) - (parseFloat(n.deductions) || 0);
    return n;
  });

  const handleFile = (e) => {
    const file = e.target.files?.[0];
    if (!file) return;
    const reader = new FileReader();
    reader.onload = () => set('receipt_base64', reader.result);
    reader.readAsDataURL(file);
  };

  const submit = async () => {
    if (!form.month) return;
    await api.post(`/employees/${employeeId}/salaries`, form);
    setForm({ month: '', year: new Date().getFullYear(), base_salary: 0, extras: 0, deductions: 0, net_salary: 0, receipt_base64: '' });
    onDone();
  };

  return (
    <div className="grid grid-cols-2 md:grid-cols-4 gap-2">
      <select className="select" value={form.month} onChange={e => set('month', e.target.value)}>
        <option value="">Mes...</option>
        {['Jan','Fev','Mar','Abr','Mai','Jun','Jul','Ago','Set','Out','Nov','Dez'].map((m, i) => <option key={i} value={String(i+1).padStart(2,'0')}>{m}</option>)}
      </select>
      <input className="input" type="number" placeholder="Ano" value={form.year} onChange={e => set('year', parseInt(e.target.value) || 0)} />
      <input className="input" type="number" placeholder="Salario Base" value={form.base_salary} onChange={e => set('base_salary', parseFloat(e.target.value) || 0)} />
      <input className="input" type="number" placeholder="Extras" value={form.extras} onChange={e => set('extras', parseFloat(e.target.value) || 0)} />
      <input className="input" type="number" placeholder="Descontos" value={form.deductions} onChange={e => set('deductions', parseFloat(e.target.value) || 0)} />
      <div className="flex items-center gap-1 p-1.5 rounded" style={{ background: 'var(--surface-alt)' }}><span className="text-xs">Liq:</span><span className="text-xs font-bold">{form.net_salary.toFixed(2)} EUR</span></div>
      <label className="btn btn-outline btn-sm cursor-pointer flex items-center gap-1">
        <FileText size={12} /> {form.receipt_base64 ? 'Recibo OK' : 'Upload Recibo'}
        <input type="file" accept=".pdf,.jpg,.png" onChange={handleFile} className="hidden" />
      </label>
      <button data-testid="add-salary-btn" onClick={submit} className="btn btn-primary btn-sm">Registar</button>
    </div>
  );
}

function EmployeeForm({ employee, onSave, onCancel }) {
  const [form, setForm] = useState(employee || {
    name: '', email: '', phone: '', role: '', status: 'ativo',
    contract_type: 'efetivo', contract_start: '', contract_end: '', salary: 0,
    nif: '', niss: '', address: '', emergency_contact: '', emergency_phone: '',
    birth_date: '', notes: '',
    work_insurance: '', work_insurance_policy: '', work_insurance_expiry: '',
    health_insurance: '', health_insurance_policy: '',
    medical_exam_date: '', medical_exam_expiry: '', medical_exam_result: 'apto',
    safety_training_date: '', safety_training_expiry: '',
    ppe_delivered: [], certifications: [],
    id_document: '', id_expiry: '', driving_license: '', driving_license_expiry: ''
  });
  const [tab, setTab] = useState('pessoal');
  const set = (k, v) => setForm(p => ({ ...p, [k]: v }));

  return (
    <div className="animate-in pt-10 lg:pt-0">
      <div className="flex items-center justify-between mb-4">
        <h1 className="text-xl font-bold">{employee ? 'Editar Funcionario' : 'Novo Funcionario'}</h1>
        <button data-testid="close-employee-form" onClick={onCancel} className="btn btn-outline btn-sm"><X size={16} /></button>
      </div>

      <div className="flex gap-0 mb-4 overflow-x-auto" style={{ borderBottom: '1px solid var(--border)' }}>
        {[
          { id: 'pessoal', label: 'Dados Pessoais' },
          { id: 'contrato', label: 'Contrato' },
          { id: 'seguros', label: 'Seguros' },
          { id: 'seguranca', label: 'Higiene & Seguranca' },
          { id: 'documentos', label: 'Documentos' },
        ].map(t => (
          <button key={t.id} onClick={() => setTab(t.id)} className={`tab-btn ${tab === t.id ? 'active' : ''}`}>{t.label}</button>
        ))}
      </div>

      <div className="pro-card-padded">
        {tab === 'pessoal' && (
          <div className="grid grid-cols-1 md:grid-cols-3 gap-3">
            <div><label className="field-label">Nome</label><input data-testid="employee-name" className="input" value={form.name} onChange={e => set('name', e.target.value)} /></div>
            <div><label className="field-label">Email</label><input data-testid="employee-email" className="input" value={form.email} onChange={e => set('email', e.target.value)} /></div>
            <div><label className="field-label">Telefone</label><input data-testid="employee-phone" className="input" value={form.phone} onChange={e => set('phone', e.target.value)} /></div>
            <div><label className="field-label">Funcao</label><input className="input" placeholder="Pedreiro, Eletricista..." value={form.role} onChange={e => set('role', e.target.value)} /></div>
            <div><label className="field-label">Data Nascimento</label><input className="input" type="date" value={form.birth_date} onChange={e => set('birth_date', e.target.value)} /></div>
            <div><label className="field-label">Estado</label>
              <select className="select" value={form.status} onChange={e => set('status', e.target.value)}>
                <option value="ativo">Ativo</option><option value="ferias">Ferias</option><option value="baixa">Baixa</option><option value="inativo">Inativo</option>
              </select></div>
            <div className="md:col-span-2"><label className="field-label">Morada</label><input className="input" value={form.address} onChange={e => set('address', e.target.value)} /></div>
            <div><label className="field-label">NIF</label><input className="input" value={form.nif} onChange={e => set('nif', e.target.value)} /></div>
            <div><label className="field-label">NISS</label><input className="input" value={form.niss} onChange={e => set('niss', e.target.value)} /></div>
            <div><label className="field-label">Contacto Emergencia</label><input className="input" value={form.emergency_contact} onChange={e => set('emergency_contact', e.target.value)} /></div>
            <div><label className="field-label">Tel. Emergencia</label><input className="input" value={form.emergency_phone} onChange={e => set('emergency_phone', e.target.value)} /></div>
          </div>
        )}

        {tab === 'contrato' && (
          <div className="grid grid-cols-1 md:grid-cols-3 gap-3">
            <div><label className="field-label">Tipo Contrato</label>
              <select className="select" value={form.contract_type} onChange={e => set('contract_type', e.target.value)}>
                <option value="efetivo">Efetivo</option><option value="termo_certo">Termo Certo</option><option value="termo_incerto">Termo Incerto</option><option value="prestacao_servicos">Prestacao Servicos</option><option value="estagio">Estagio</option>
              </select></div>
            <div><label className="field-label">Inicio Contrato</label><input className="input" type="date" value={form.contract_start} onChange={e => set('contract_start', e.target.value)} /></div>
            <div><label className="field-label">Fim Contrato</label><input className="input" type="date" value={form.contract_end} onChange={e => set('contract_end', e.target.value)} /></div>
            <div><label className="field-label">Salario Bruto (EUR)</label><input className="input" type="number" value={form.salary} onChange={e => set('salary', parseFloat(e.target.value) || 0)} /></div>
            <div className="md:col-span-2"><label className="field-label">IBAN</label><input className="input font-mono" placeholder="PT50 0000 0000 0000 0000 0000 0" value={form.iban || ''} onChange={e => set('iban', e.target.value)} /></div>
          </div>
        )}

        {tab === 'seguros' && (
          <div className="grid grid-cols-1 md:grid-cols-3 gap-3">
            <div><label className="field-label">Seguro Trabalho</label><input className="input" placeholder="Seguradora" value={form.work_insurance} onChange={e => set('work_insurance', e.target.value)} /></div>
            <div><label className="field-label">Apolice Trabalho</label><input className="input" value={form.work_insurance_policy} onChange={e => set('work_insurance_policy', e.target.value)} /></div>
            <div><label className="field-label">Validade Seg. Trabalho</label><input className="input" type="date" value={form.work_insurance_expiry} onChange={e => set('work_insurance_expiry', e.target.value)} /></div>
            <div><label className="field-label">Seguro Saude</label><input className="input" placeholder="Seguradora" value={form.health_insurance} onChange={e => set('health_insurance', e.target.value)} /></div>
            <div><label className="field-label">Apolice Saude</label><input className="input" value={form.health_insurance_policy} onChange={e => set('health_insurance_policy', e.target.value)} /></div>
          </div>
        )}

        {tab === 'seguranca' && (
          <div className="grid grid-cols-1 md:grid-cols-3 gap-3">
            <div><label className="field-label">Data Exame Medico</label><input className="input" type="date" value={form.medical_exam_date} onChange={e => set('medical_exam_date', e.target.value)} /></div>
            <div><label className="field-label">Validade Exame</label><input className="input" type="date" value={form.medical_exam_expiry} onChange={e => set('medical_exam_expiry', e.target.value)} /></div>
            <div><label className="field-label">Resultado</label>
              <select className="select" value={form.medical_exam_result} onChange={e => set('medical_exam_result', e.target.value)}>
                <option value="apto">Apto</option><option value="apto_condicionado">Apto Condicionado</option><option value="inapto">Inapto</option><option value="pendente">Pendente</option>
              </select></div>
            <div><label className="field-label">Formacao Seguranca</label><input className="input" type="date" value={form.safety_training_date} onChange={e => set('safety_training_date', e.target.value)} /></div>
            <div><label className="field-label">Validade Formacao</label><input className="input" type="date" value={form.safety_training_expiry} onChange={e => set('safety_training_expiry', e.target.value)} /></div>
            <div className="md:col-span-3">
              <label className="field-label">Certificacoes (separar por virgula)</label>
              <input className="input" placeholder="Trabalhos em Altura, Eletricidade, Plataformas Elevatórias..." value={(form.certifications || []).join(', ')} onChange={e => set('certifications', e.target.value.split(',').map(s => s.trim()).filter(Boolean))} />
            </div>
            <div className="md:col-span-3">
              <label className="field-label">EPIs Entregues (separar por virgula)</label>
              <input className="input" placeholder="Capacete, Botas, Colete, Luvas, Oculos..." value={(form.ppe_delivered || []).join(', ')} onChange={e => set('ppe_delivered', e.target.value.split(',').map(s => s.trim()).filter(Boolean))} />
            </div>
          </div>
        )}

        {tab === 'documentos' && (
          <div className="grid grid-cols-1 md:grid-cols-3 gap-3">
            <div><label className="field-label">Documento ID</label><input className="input" placeholder="CC / BI" value={form.id_document} onChange={e => set('id_document', e.target.value)} /></div>
            <div><label className="field-label">Validade ID</label><input className="input" type="date" value={form.id_expiry} onChange={e => set('id_expiry', e.target.value)} /></div>
            <div></div>
            <div><label className="field-label">Carta Conducao</label><input className="input" value={form.driving_license} onChange={e => set('driving_license', e.target.value)} /></div>
            <div><label className="field-label">Validade Carta</label><input className="input" type="date" value={form.driving_license_expiry} onChange={e => set('driving_license_expiry', e.target.value)} /></div>
          </div>
        )}

        <div className="flex gap-2 justify-end mt-6">
          <button data-testid="cancel-employee-btn" onClick={onCancel} className="btn btn-outline">Cancelar</button>
          <button data-testid="save-employee-btn" onClick={() => onSave(form)} className="btn btn-primary">Guardar</button>
        </div>
      </div>
    </div>
  );
}

function EmployeeDetail({ employee, onBack, onRefresh }) {
  const [tab, setTab] = useState('resumo');
  const [hoursForm, setHoursForm] = useState({ date: '', hours: 0, project_name: '', notes: '' });
  const [advForm, setAdvForm] = useState({ date: '', amount: 0, notes: '', payment_method: 'dinheiro', advance_type: 'adiantamento', description: '' });
  const [absForm, setAbsForm] = useState({ date: '', type: 'falta', justified: false, notes: '' });
  const [expForm, setExpForm] = useState({ date: '', description: '', amount: 0, category: 'alimentacao', paid_by_company: false, notes: '' });

  const addHours = async () => { await api.post(`/employees/${employee.id}/hours`, hoursForm); setHoursForm({ date: '', hours: 0, project_name: '', notes: '' }); onRefresh(); };
  const addAdv = async () => { await api.post(`/employees/${employee.id}/advances`, advForm); setAdvForm({ date: '', amount: 0, notes: '', payment_method: 'dinheiro', advance_type: 'adiantamento', description: '' }); onRefresh(); };
  const addAbs = async () => { await api.post(`/employees/${employee.id}/absences`, absForm); setAbsForm({ date: '', type: 'falta', justified: false, notes: '' }); onRefresh(); };
  const addExp = async () => { await api.post(`/employees/${employee.id}/expenses`, expForm); setExpForm({ date: '', description: '', amount: 0, category: 'alimentacao', paid_by_company: false, notes: '' }); onRefresh(); };

  const totalH = (employee.hours_log || []).reduce((s, h) => s + (h.hours || 0), 0);
  const totalAdv = (employee.advances || []).reduce((s, a) => s + (a.amount || 0), 0);
  const totalAbs = (employee.absences || []).length;
  const totalExp = (employee.expenses || []).reduce((s, e) => s + (e.amount || 0), 0);

  const alerts = [];
  if (isExpired(employee.medical_exam_expiry)) alerts.push('Exame medico expirado');
  if (isExpired(employee.safety_training_expiry)) alerts.push('Formacao seguranca expirada');
  if (isExpired(employee.work_insurance_expiry)) alerts.push('Seguro trabalho expirado');
  if (isExpired(employee.id_expiry)) alerts.push('Documento ID expirado');
  if (isExpiringSoon(employee.medical_exam_expiry)) alerts.push('Exame medico a expirar');
  if (isExpiringSoon(employee.work_insurance_expiry)) alerts.push('Seguro trabalho a expirar');

  return (
    <div className="animate-in pt-10 lg:pt-0">
      <button onClick={onBack} className="text-xs font-medium cursor-pointer mb-1" style={{ color: 'var(--text-light)', background: 'none', border: 'none' }}>&#8592; Voltar</button>
      <div className="flex items-center justify-between mb-1">
        <h1 className="text-xl font-bold">{employee.name}</h1>
        <span className={`badge ${employee.status === 'ativo' ? 'badge-success' : 'badge-warning'}`}>{employee.status}</span>
      </div>
      <p className="text-sm mb-4" style={{ color: 'var(--text-secondary)' }}>{employee.role} | {employee.contract_type} | {employee.salary ? `${employee.salary} EUR` : 'N/D'}</p>

      {alerts.length > 0 && (
        <div className="p-3 rounded-md mb-4" style={{ background: 'var(--warning-bg)', border: '1px solid #FFD180' }}>
          {alerts.map((a, i) => <p key={i} className="text-xs font-medium flex items-center gap-1" style={{ color: 'var(--warning)' }}><AlertTriangle size={12} /> {a}</p>)}
        </div>
      )}

      <div className="grid grid-cols-2 md:grid-cols-4 gap-3 mb-4">
        <div className="metric-card"><div className="flex items-center gap-1.5 mb-1"><Clock size={14} style={{ color: 'var(--info)' }} /><span className="metric-label">Horas</span></div><p className="metric-value text-lg">{totalH}h</p></div>
        <div className="metric-card"><div className="flex items-center gap-1.5 mb-1"><DollarSign size={14} style={{ color: 'var(--warning)' }} /><span className="metric-label">Adiantamentos</span></div><p className="metric-value text-lg">{totalAdv} EUR</p></div>
        <div className="metric-card"><div className="flex items-center gap-1.5 mb-1"><CalendarX size={14} style={{ color: 'var(--error)' }} /><span className="metric-label">Faltas</span></div><p className="metric-value text-lg">{totalAbs}</p></div>
        <div className="metric-card"><div className="flex items-center gap-1.5 mb-1"><Receipt size={14} style={{ color: 'var(--accent)' }} /><span className="metric-label">Despesas</span></div><p className="metric-value text-lg">{totalExp} EUR</p></div>
        <div className="metric-card"><div className="flex items-center gap-1.5 mb-1"><ShieldCheck size={14} style={{ color: 'var(--success)' }} /><span className="metric-label">Exame Med.</span></div><p className="text-sm font-semibold">{employee.medical_exam_result || 'N/D'}</p></div>
      </div>

      <div className="flex gap-0 mb-4 overflow-x-auto" style={{ borderBottom: '1px solid var(--border)' }}>
        {[{ id: 'resumo', label: 'Resumo' }, { id: 'salarios', label: 'Salarios' }, { id: 'horas', label: 'Horas' }, { id: 'adiantamentos', label: 'Adiant.' }, { id: 'faltas', label: 'Faltas' }, { id: 'despesas', label: 'Despesas' }].map(t => (
          <button key={t.id} onClick={() => setTab(t.id)} className={`tab-btn ${tab === t.id ? 'active' : ''}`}>{t.label}</button>
        ))}
      </div>

      {tab === 'resumo' && (
        <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
          <div className="pro-card-padded">
            <h3 className="text-sm font-semibold mb-2 flex items-center gap-1"><Heart size={14} /> Seguros</h3>
            <div className="space-y-1 text-xs">
              <p><span className="font-medium">Seg. Trabalho:</span> {employee.work_insurance || 'N/D'} {employee.work_insurance_expiry && `(val. ${employee.work_insurance_expiry})`}</p>
              <p><span className="font-medium">Seg. Saude:</span> {employee.health_insurance || 'N/D'}</p>
            </div>
          </div>
          <div className="pro-card-padded">
            <h3 className="text-sm font-semibold mb-2 flex items-center gap-1"><ShieldCheck size={14} /> Higiene & Seguranca</h3>
            <div className="space-y-1 text-xs">
              <p><span className="font-medium">Exame Medico:</span> {employee.medical_exam_expiry || 'N/D'} - {employee.medical_exam_result}</p>
              <p><span className="font-medium">Form. Seguranca:</span> {employee.safety_training_expiry || 'N/D'}</p>
              {employee.certifications?.length > 0 && <p><span className="font-medium">Certificacoes:</span> {employee.certifications.join(', ')}</p>}
              {employee.ppe_delivered?.length > 0 && <p><span className="font-medium">EPIs:</span> {employee.ppe_delivered.join(', ')}</p>}
            </div>
          </div>
          <div className="pro-card-padded">
            <h3 className="text-sm font-semibold mb-2 flex items-center gap-1"><FileText size={14} /> Documentos</h3>
            <div className="space-y-1 text-xs">
              <p><span className="font-medium">Doc. ID:</span> {employee.id_document || 'N/D'} {employee.id_expiry && `(val. ${employee.id_expiry})`}</p>
              <p><span className="font-medium">Carta:</span> {employee.driving_license || 'N/D'} {employee.driving_license_expiry && `(val. ${employee.driving_license_expiry})`}</p>
              <p><span className="font-medium">NIF:</span> {employee.nif || 'N/D'}</p>
              <p><span className="font-medium">NISS:</span> {employee.niss || 'N/D'}</p>
            </div>
          </div>
          <div className="pro-card-padded">
            <h3 className="text-sm font-semibold mb-2">Contacto</h3>
            <div className="space-y-1 text-xs">
              <p>{employee.email}</p><p>{employee.phone}</p>
              <p><span className="font-medium">Emergencia:</span> {employee.emergency_contact} {employee.emergency_phone}</p>
            </div>
          </div>
        </div>
      )}

      {tab === 'salarios' && (
        <div className="pro-card-padded">
          <div className="mb-3">
            <div className="flex items-center justify-between mb-2">
              <h3 className="text-sm font-semibold">IBAN e Dados Pagamento</h3>
            </div>
            <div className="grid grid-cols-1 md:grid-cols-2 gap-2 mb-3">
              <div>
                <label className="field-label">IBAN</label>
                <input data-testid="employee-iban" className="input font-mono" placeholder="PT50 0000 0000 0000 0000 0000 0" value={employee.iban || ''} onChange={e => {
                  api.put(`/employees/${employee.id}`, { iban: e.target.value }).then(() => onRefresh());
                }} />
              </div>
              <div>
                <label className="field-label">Salario Base</label>
                <input className="input" type="number" value={employee.salary || 0} readOnly style={{ background: 'var(--surface-alt)' }} />
              </div>
            </div>
          </div>
          <h3 className="text-sm font-semibold mb-2">Registar Recibo</h3>
          <SalaryForm employeeId={employee.id} onDone={onRefresh} />
          {employee.salaries?.length > 0 ? (
            <div className="overflow-x-auto mt-3">
              <table className="pro-table" data-testid="salary-table">
                <thead><tr><th>Mes/Ano</th><th>Base</th><th>Extras</th><th>Descontos</th><th>Liquido</th><th>Estado Pagamento</th><th>Recibo</th></tr></thead>
                <tbody>
                  {employee.salaries.map(s => (
                    <tr key={s.id}>
                      <td className="font-medium">{s.month}/{s.year}</td>
                      <td>{(s.base_salary || 0).toFixed(2)} EUR</td>
                      <td className="text-green-600">+{(s.extras || 0).toFixed(2)}</td>
                      <td className="text-red-600">-{(s.deductions || 0).toFixed(2)}</td>
                      <td className="font-bold">{(s.net_salary || 0).toFixed(2)} EUR</td>
                      <td>
                        <div className="flex items-center gap-2">
                          {/* Toggle switch */}
                          <button
                            data-testid={`salary-paid-toggle-${s.id}`}
                            onClick={() => {
                              const newPaid = !s.paid;
                              api.put(`/employees/${employee.id}/salaries/${s.id}`, { paid: newPaid, paid_date: newPaid ? new Date().toISOString().split('T')[0] : '' }).then(() => onRefresh());
                            }}
                            className="relative inline-flex h-5 w-9 items-center rounded-full transition-colors focus:outline-none"
                            style={{ background: s.paid ? '#0B7A3E' : '#CBD5E0' }}
                          >
                            <span
                              className="inline-block h-3.5 w-3.5 transform rounded-full bg-white transition-transform"
                              style={{ transform: s.paid ? 'translateX(18px)' : 'translateX(2px)' }}
                            />
                          </button>
                          <span className="text-xs font-medium" style={{ color: s.paid ? '#0B7A3E' : 'var(--text-secondary)' }}>
                            {s.paid ? `Pago${s.paid_date ? ' '+s.paid_date : ''}` : 'Por pagar'}
                          </span>
                        </div>
                      </td>
                      <td>
                        {s.receipt_base64 ? (
                          <button
                            onClick={() => {
                              // Open receipt in new tab
                              const win = window.open();
                              if (win) {
                                win.document.write(`<html><body style="margin:0"><img src="${s.receipt_base64.startsWith('data:') ? s.receipt_base64 : 'data:image/jpeg;base64,' + s.receipt_base64}" style="max-width:100%;height:auto" /><p style="font-family:sans-serif;font-size:12px;padding:10px">Recibo salario ${s.month}/${s.year}</p></body></html>`);
                                win.document.close();
                              }
                            }}
                            className="badge badge-success cursor-pointer hover:opacity-80"
                            title="Clique para ver recibo"
                          >
                            Ver Recibo
                          </button>
                        ) : (
                          <label className="btn btn-outline btn-xs cursor-pointer" title="Upload recibo">
                            + Recibo
                            <input type="file" accept=".pdf,.jpg,.jpeg,.png" className="hidden" onChange={e => {
                              const file = e.target.files?.[0];
                              if (!file) return;
                              const reader = new FileReader();
                              reader.onload = () => api.put(`/employees/${employee.id}/salaries/${s.id}`, { receipt_base64: reader.result }).then(() => onRefresh());
                              reader.readAsDataURL(file);
                            }} />
                          </label>
                        )}
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          ) : <p className="text-xs mt-3" style={{ color: 'var(--text-light)' }}>Sem registos de salario</p>}
        </div>
      )}

      {tab === 'horas' && (
        <div className="pro-card-padded">
          <div className="grid grid-cols-2 md:grid-cols-5 gap-2 mb-3">
            <input className="input" type="date" value={hoursForm.date} onChange={e => setHoursForm(p => ({ ...p, date: e.target.value }))} />
            <input className="input" type="number" placeholder="Horas" value={hoursForm.hours} onChange={e => setHoursForm(p => ({ ...p, hours: parseFloat(e.target.value) || 0 }))} />
            <input className="input" placeholder="Projeto" value={hoursForm.project_name} onChange={e => setHoursForm(p => ({ ...p, project_name: e.target.value }))} />
            <input className="input" placeholder="Notas" value={hoursForm.notes} onChange={e => setHoursForm(p => ({ ...p, notes: e.target.value }))} />
            <button data-testid="add-hours-btn" onClick={addHours} className="btn btn-primary">Adicionar</button>
          </div>
          {employee.hours_log?.length > 0 && (
            <table className="pro-table"><thead><tr><th>Data</th><th>Horas</th><th>Projeto</th><th>Notas</th></tr></thead>
              <tbody>{employee.hours_log.map((h, i) => <tr key={h.id || i}><td>{h.date}</td><td className="font-semibold">{h.hours}h</td><td>{h.project_name}</td><td>{h.notes}</td></tr>)}</tbody></table>
          )}
        </div>
      )}

      {tab === 'adiantamentos' && (
        <div className="pro-card-padded">
          <div className="grid grid-cols-2 md:grid-cols-6 gap-2 mb-3">
            <input className="input" type="date" value={advForm.date} onChange={e => setAdvForm(p => ({ ...p, date: e.target.value }))} />
            <input className="input" type="number" placeholder="Valor EUR" value={advForm.amount} onChange={e => setAdvForm(p => ({ ...p, amount: parseFloat(e.target.value) || 0 }))} />
            <select className="select" value={advForm.payment_method || 'dinheiro'} onChange={e => setAdvForm(p => ({ ...p, payment_method: e.target.value }))}>
              <option value="dinheiro">Dinheiro</option>
              <option value="transferencia">Transferencia</option>
              <option value="mbway">MBWay</option>
            </select>
            <select className="select" value={advForm.advance_type || 'adiantamento'} onChange={e => setAdvForm(p => ({ ...p, advance_type: e.target.value }))}>
              <option value="adiantamento">Adiantamento</option>
              <option value="despesa">Despesa</option>
            </select>
            <input className="input" placeholder="Descricao" value={advForm.description || ''} onChange={e => setAdvForm(p => ({ ...p, description: e.target.value }))} />
            <button data-testid="add-advance-btn" onClick={addAdv} className="btn btn-primary">Adicionar</button>
          </div>
          <input className="input mb-3" placeholder="Notas (opcional)" value={advForm.notes} onChange={e => setAdvForm(p => ({ ...p, notes: e.target.value }))} />
          
          {employee.advances?.length > 0 ? (
            <div className="overflow-x-auto">
              <table className="pro-table">
                <thead>
                  <tr>
                    <th>Data</th>
                    <th>Tipo</th>
                    <th>Valor</th>
                    <th>Metodo</th>
                    <th>Descricao</th>
                    <th>Notas</th>
                    <th></th>
                  </tr>
                </thead>
                <tbody>
                  {employee.advances.map((a, i) => (
                    <tr key={a.id || i}>
                      <td>{a.date}</td>
                      <td>
                        <span className={`badge ${a.advance_type === 'despesa' ? 'badge-error' : 'badge-info'}`}>
                          {a.advance_type === 'despesa' ? 'Despesa' : 'Adiantamento'}
                        </span>
                      </td>
                      <td className="font-semibold">{a.amount} EUR</td>
                      <td>
                        <span className="badge badge-neutral">
                          {a.payment_method === 'transferencia' ? 'Transf.' : a.payment_method === 'mbway' ? 'MBWay' : 'Dinheiro'}
                        </span>
                      </td>
                      <td className="text-xs">{a.description || '-'}</td>
                      <td className="text-xs" style={{ color: 'var(--text-light)' }}>{a.notes || '-'}</td>
                      <td>
                        <div className="flex gap-1">
                          <button 
                            onClick={async () => {
                              const newAmount = window.prompt('Novo valor:', a.amount);
                              if (newAmount !== null && parseFloat(newAmount) !== a.amount) {
                                await api.put(`/employees/${employee.id}/advances/${a.id}`, { amount: parseFloat(newAmount) || 0 });
                                onRefresh();
                              }
                            }}
                            className="btn btn-outline btn-xs"
                            title="Editar"
                          >
                            Editar
                          </button>
                          <button 
                            onClick={async () => {
                              if (window.confirm('Apagar adiantamento?')) {
                                await api.delete(`/employees/${employee.id}/advances/${a.id}`);
                                onRefresh();
                              }
                            }}
                            className="btn btn-danger btn-xs"
                            title="Apagar"
                          >
                            X
                          </button>
                        </div>
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          ) : <p className="text-xs mt-3" style={{ color: 'var(--text-light)' }}>Sem adiantamentos</p>}
          
          {/* Summary */}
          {employee.advances?.length > 0 && (
            <div className="mt-4 p-3 rounded-md" style={{ background: 'var(--surface-alt)', border: '1px solid var(--border-light)' }}>
              <div className="grid grid-cols-2 md:grid-cols-4 gap-3 text-center">
                <div>
                  <p className="text-[10px] font-medium" style={{ color: 'var(--text-light)' }}>Total Adiantamentos</p>
                  <p className="text-sm font-bold" style={{ color: 'var(--info)' }}>
                    {employee.advances.filter(a => a.advance_type !== 'despesa').reduce((s, a) => s + (a.amount || 0), 0).toLocaleString('pt-PT')} EUR
                  </p>
                </div>
                <div>
                  <p className="text-[10px] font-medium" style={{ color: 'var(--text-light)' }}>Total Despesas</p>
                  <p className="text-sm font-bold" style={{ color: 'var(--error)' }}>
                    {employee.advances.filter(a => a.advance_type === 'despesa').reduce((s, a) => s + (a.amount || 0), 0).toLocaleString('pt-PT')} EUR
                  </p>
                </div>
                <div>
                  <p className="text-[10px] font-medium" style={{ color: 'var(--text-light)' }}>Por Dinheiro</p>
                  <p className="text-sm font-semibold">
                    {employee.advances.filter(a => a.payment_method === 'dinheiro').reduce((s, a) => s + (a.amount || 0), 0).toLocaleString('pt-PT')} EUR
                  </p>
                </div>
                <div>
                  <p className="text-[10px] font-medium" style={{ color: 'var(--text-light)' }}>Por Transf/MBWay</p>
                  <p className="text-sm font-semibold">
                    {employee.advances.filter(a => a.payment_method !== 'dinheiro').reduce((s, a) => s + (a.amount || 0), 0).toLocaleString('pt-PT')} EUR
                  </p>
                </div>
              </div>
            </div>
          )}
        </div>
      )}

      {tab === 'faltas' && (
        <div className="pro-card-padded">
          <div className="grid grid-cols-2 md:grid-cols-5 gap-2 mb-3">
            <input className="input" type="date" value={absForm.date} onChange={e => setAbsForm(p => ({ ...p, date: e.target.value }))} />
            <select className="select" value={absForm.type} onChange={e => setAbsForm(p => ({ ...p, type: e.target.value }))}>
              <option value="falta">Falta</option><option value="ferias">Ferias</option><option value="baixa">Baixa</option>
            </select>
            <label className="flex items-center gap-2 text-xs font-medium">
              <input type="checkbox" checked={absForm.justified} onChange={e => setAbsForm(p => ({ ...p, justified: e.target.checked }))} className="w-4 h-4 rounded" /> Justificada
            </label>
            <input className="input" placeholder="Notas" value={absForm.notes} onChange={e => setAbsForm(p => ({ ...p, notes: e.target.value }))} />
            <button data-testid="add-absence-btn" onClick={addAbs} className="btn btn-primary">Adicionar</button>
          </div>
          {employee.absences?.length > 0 && (
            <table className="pro-table"><thead><tr><th>Data</th><th>Tipo</th><th>Just.</th><th>Notas</th></tr></thead>
              <tbody>{employee.absences.map((a, i) => <tr key={a.id || i}><td>{a.date}</td><td className="font-medium">{a.type}</td><td>{a.justified ? 'Sim' : 'Nao'}</td><td>{a.notes}</td></tr>)}</tbody></table>
          )}
        </div>
      )}

      {tab === 'despesas' && (
        <div className="pro-card-padded">
          <div className="grid grid-cols-2 md:grid-cols-6 gap-2 mb-3">
            <input className="input" type="date" value={expForm.date} onChange={e => setExpForm(p => ({ ...p, date: e.target.value }))} />
            <input className="input" placeholder="Descricao" value={expForm.description} onChange={e => setExpForm(p => ({ ...p, description: e.target.value }))} />
            <input className="input" type="number" placeholder="Valor EUR" value={expForm.amount} onChange={e => setExpForm(p => ({ ...p, amount: parseFloat(e.target.value) || 0 }))} />
            <select className="select" value={expForm.category} onChange={e => setExpForm(p => ({ ...p, category: e.target.value }))}>
              <option value="alimentacao">Alimentacao</option><option value="transporte">Transporte</option><option value="alojamento">Alojamento</option><option value="material">Material</option><option value="equipamento">Equipamento</option><option value="outro">Outro</option>
            </select>
            <label className="flex items-center gap-1 text-xs font-medium"><input type="checkbox" checked={expForm.paid_by_company} onChange={e => setExpForm(p => ({ ...p, paid_by_company: e.target.checked }))} className="w-4 h-4 rounded" /> Paga empresa</label>
            <button data-testid="add-expense-btn" onClick={addExp} className="btn btn-primary">Adicionar</button>
          </div>
          {employee.expenses?.length > 0 && (
            <table className="pro-table"><thead><tr><th>Data</th><th>Descricao</th><th>Categoria</th><th>Valor</th><th>Paga Empresa</th></tr></thead>
              <tbody>{employee.expenses.map((e, i) => <tr key={e.id || i}><td>{e.date}</td><td>{e.description}</td><td><span className="badge badge-neutral">{e.category}</span></td><td className="font-semibold">{e.amount} EUR</td><td>{e.paid_by_company ? 'Sim' : 'Nao'}</td></tr>)}</tbody></table>
          )}
        </div>
      )}
    </div>
  );
}

export default function EmployeesPage() {
  const [employees, setEmployees] = useState([]);
  const [editing, setEditing] = useState(null);
  const [creating, setCreating] = useState(false);
  const [viewing, setViewing] = useState(null);
  const [search, setSearch] = useState('');
  const [filterStatus, setFilterStatus] = useState('');
  const [filterContract, setFilterContract] = useState('');

  const load = useCallback(() => { api.get('/employees').then(r => setEmployees(r.data)).catch(() => {}); }, []);
  useEffect(() => { load(); }, [load]);

  const handleSave = async (form) => {
    if (editing) await api.put(`/employees/${editing.id}`, form);
    else await api.post('/employees', form);
    setEditing(null); setCreating(false); load();
  };

  const handleDelete = async (id) => { if (window.confirm('Eliminar?')) { await api.delete(`/employees/${id}`); load(); } };

  const refreshViewing = async () => {
    if (!viewing) return;
    const { data } = await api.get('/employees');
    setViewing(data.find(e => e.id === viewing.id) || null);
    setEmployees(data);
  };

  const filtered = employees.filter(e => {
    if (filterStatus && e.status !== filterStatus) return false;
    if (filterContract && e.contract_type !== filterContract) return false;
    if (search && ![e.name, e.role, e.email].some(f => f?.toLowerCase().includes(search.toLowerCase()))) return false;
    return true;
  });

  if (viewing) return <EmployeeDetail employee={viewing} onBack={() => { setViewing(null); load(); }} onRefresh={refreshViewing} />;
  if (creating || editing) return <EmployeeForm employee={editing} onSave={handleSave} onCancel={() => { setEditing(null); setCreating(false); }} />;

  return (
    <div className="animate-in pt-10 lg:pt-0">
      <div className="flex flex-col sm:flex-row items-start sm:items-center justify-between gap-3 mb-4">
        <div>
          <h1 className="text-xl font-bold">Funcionarios</h1>
          <p className="text-sm" style={{ color: 'var(--text-secondary)' }}>{employees.length} registo{employees.length !== 1 ? 's' : ''}</p>
        </div>
        <button data-testid="new-employee-btn" onClick={() => setCreating(true)} className="btn btn-primary"><Plus size={16} /> Novo Funcionario</button>
      </div>
      <div className="mb-4 flex gap-2 flex-wrap">
        <div className="relative flex-1 min-w-[150px]">
          <Search size={16} className="absolute left-3 top-1/2 -translate-y-1/2" style={{ color: 'var(--text-light)' }} />
          <input data-testid="employee-search" className="input pl-9" placeholder="Pesquisar..." value={search} onChange={e => setSearch(e.target.value)} />
        </div>
        <select data-testid="employee-filter-status" className="select" style={{ width: 'auto' }} value={filterStatus} onChange={e => setFilterStatus(e.target.value)}>
          <option value="">Todos os estados</option>
          <option value="ativo">Ativo</option>
          <option value="ferias">Ferias</option>
          <option value="baixa">Baixa</option>
          <option value="inativo">Inativo</option>
        </select>
        <select data-testid="employee-filter-contract" className="select" style={{ width: 'auto' }} value={filterContract} onChange={e => setFilterContract(e.target.value)}>
          <option value="">Todos os contratos</option>
          <option value="efetivo">Efetivo</option>
          <option value="termo_certo">Termo Certo</option>
          <option value="termo_incerto">Termo Incerto</option>
          <option value="prestacao_servicos">Prestacao Servicos</option>
          <option value="estagio">Estagio</option>
        </select>
      </div>
      {filtered.length === 0 ? (
        <div className="pro-card-padded text-center py-12"><Users size={40} className="mx-auto mb-3" style={{ color: 'var(--text-light)' }} /><p className="font-semibold">Sem funcionarios</p></div>
      ) : (
        <div className="pro-card overflow-hidden">
          <div className="overflow-x-auto">
            <table className="pro-table" data-testid="employees-table">
              <thead><tr><th>Nome</th><th>Funcao</th><th>Contrato</th><th>Telefone</th><th>Estado</th><th>Alertas</th><th>Acoes</th></tr></thead>
              <tbody>
                {filtered.map(emp => {
                  const hasAlert = isExpired(emp.medical_exam_expiry) || isExpired(emp.work_insurance_expiry) || isExpired(emp.safety_training_expiry);
                  return (
                    <tr key={emp.id} data-testid={`employee-row-${emp.id}`} className="cursor-pointer" onClick={() => setViewing(emp)}>
                      <td className="font-medium">{emp.name}</td>
                      <td className="text-xs">{emp.role || '-'}</td>
                      <td className="text-xs">{emp.contract_type}</td>
                      <td className="text-xs">{emp.phone}</td>
                      <td><span className={`badge ${emp.status === 'ativo' ? 'badge-success' : emp.status === 'ferias' ? 'badge-info' : 'badge-warning'}`}>{emp.status}</span></td>
                      <td>{hasAlert && <AlertTriangle size={14} color="var(--error)" />}</td>
                      <td>
                        <div className="flex gap-1" onClick={e => e.stopPropagation()}>
                          <button data-testid={`edit-employee-${emp.id}`} onClick={() => setEditing(emp)} className="btn btn-outline btn-xs"><Edit3 size={12} /></button>
                          <button data-testid={`delete-employee-${emp.id}`} onClick={() => handleDelete(emp.id)} className="btn btn-danger btn-xs"><Trash2 size={12} /></button>
                        </div>
                      </td>
                    </tr>
                  );
                })}
              </tbody>
            </table>
          </div>
        </div>
      )}
    </div>
  );
}
